/**
 * Host-aware FundGenius HQ login branding and post-login destinations.
 */

import { isFundGeniusAppHost, resolvePortalHost, PORTAL_HOST_KINDS } from './portalHostResolver';
import {
  getHostnamePortal,
  HOSTNAME_PORTALS,
  isPlatformConsoleRole,
} from './hostnamePortal';
import { isPlatformAdmin } from './portalAccess';
import { getFundGeniusClientsPathForUser } from './fundgeniusWorkspaceRouting';
import {
  getPortalLandingRoute,
} from './portalLandingRoute';
import {
  FUNDGENIUS_HQ_PRODUCT_NAME,
} from './funding/constants';

export const FUNDGENIUS_HQ_CLIENTS_PATH = '/platform/fundgenius-hq/clients';
export const FUNDGENIUS_APP_CLIENTS_PATH = '/fundgenius-hq/clients';

const STALE_AUTH_REDIRECT_KEYS = [
  'redirectTo',
  'returnTo',
  'postLoginRedirect',
  'redirect_to',
  'post_login_redirect',
  'auth_redirect',
  'authRedirect',
];

const FUNDELITE_HOST_MARKERS = ['fundelite.ai', 'fundelitehq.com'];

export function isFundGeniusAppLoginHost(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  if (isFundGeniusAppHost(hostname)) return true;
  const portal = getHostnamePortal(hostname);
  if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) return true;
  return resolvePortalHost(hostname).kind === PORTAL_HOST_KINDS.FUNDGENIUS_APP;
}

export function isFundGeniusSignupHost(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  if (isFundGeniusAppLoginHost(hostname)) return true;
  const portal = getHostnamePortal(hostname);
  if (
    portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING
    || portal === HOSTNAME_PORTALS.FUNDGENIUS_INTAKE
  ) {
    return true;
  }
  const kind = resolvePortalHost(hostname).kind;
  return (
    kind === PORTAL_HOST_KINDS.FUNDGENIUS_PUBLIC
    || kind === PORTAL_HOST_KINDS.FUNDGENIUS_INTAKE
  );
}

export function getFundGeniusLoginBranding(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  if (!isFundGeniusAppLoginHost(hostname)) return null;

  return {
    brandName: FUNDGENIUS_HQ_PRODUCT_NAME,
    brandMark: 'FG',
    positioning: 'Funding Operating System for Agencies',
    headline: 'Sign in to FundGenius HQ',
    subheadline:
      'Manage funding clients, applications, documents, lender strategy, and approvals from one command center.',
    valuePoints: [
      'Funding client workspace',
      'Application pipeline tracking',
      'Document vault and intake',
      'AI funding advisor',
      'Lender readiness and sequencing',
    ],
    formTitle: 'Sign in to FundGenius HQ',
    formSubtitle:
      'Manage funding clients, applications, documents, lender strategy, and approvals from one command center.',
    ctaLabel: 'Enter FundGenius HQ',
    signupPath: '/signup',
    signupLabel: 'Create a FundGenius account',
    footerCopyright: `© ${new Date().getFullYear()} FundGenius HQ. All rights reserved.`,
    testId: 'fundgenius-login-page',
  };
}

function isFundEliteAbsoluteUrl(value) {
  const raw = String(value || '').toLowerCase();
  if (!raw) return false;
  return FUNDELITE_HOST_MARKERS.some((marker) => raw.includes(marker));
}

/**
 * Clear stale cross-host redirect destinations on FundGenius hosts so a prior
 * FundElite session cannot bounce the user to app.fundelite.ai.
 */
export function clearStaleAuthRedirectStorage(
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  if (!isFundGeniusSignupHost(hostname) || typeof window === 'undefined') return;

  const stores = [window.localStorage, window.sessionStorage].filter(Boolean);
  for (const store of stores) {
    for (const key of STALE_AUTH_REDIRECT_KEYS) {
      try {
        const value = store.getItem(key);
        if (value && isFundEliteAbsoluteUrl(value)) {
          store.removeItem(key);
        }
      } catch {
        // ignore storage access errors
      }
    }
  }
}

/**
 * Sanitize backend/frontend redirect URLs for the current host.
 * On FundGenius, absolute FundElite URLs are replaced with the HQ clients path.
 */
export function sanitizeAuthRedirectUrl(
  redirectUrl,
  {
    hostname = typeof window !== 'undefined' ? window.location.hostname : '',
    origin = typeof window !== 'undefined' ? window.location.origin : '',
  } = {}
) {
  if (!redirectUrl) return null;
  const raw = String(redirectUrl).trim();
  if (!raw) return null;

  if (raw.startsWith('/')) {
    if (isFundGeniusSignupHost(hostname) && isFundEliteAbsoluteUrl(raw)) {
      return FUNDGENIUS_APP_CLIENTS_PATH;
    }
    return raw;
  }

  try {
    const url = new URL(raw, origin || undefined);
    if (isFundGeniusSignupHost(hostname)) {
      if (isFundEliteAbsoluteUrl(url.hostname) || isFundEliteAbsoluteUrl(url.href)) {
        return FUNDGENIUS_APP_CLIENTS_PATH;
      }
      if (origin && url.origin === origin) {
        return `${url.pathname}${url.search}${url.hash}` || FUNDGENIUS_HQ_CLIENTS_PATH;
      }
      return FUNDGENIUS_APP_CLIENTS_PATH;
    }
    if (origin && url.origin === origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Canonical post-signup destination by host / user context.
 * Platform admins → /platform/fundgenius-hq/clients
 * Agency owners on FundGenius host → /fundgenius-hq/clients
 */
export function getPostSignupRedirectPath({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  user = null,
  agencyType = null,
  portalType = null,
} = {}) {
  const type = String(agencyType || user?.agency_type || '').toLowerCase();
  const portal = String(portalType || user?.portal_type || '').toLowerCase();
  const isFundGeniusUser =
    type === 'fundgenius'
    || portal === 'fundgenius_hq'
    || portal === 'fundgenius'
    || isFundGeniusSignupHost(hostname);

  if (isFundGeniusUser) {
    return getFundGeniusClientsPathForUser(user, { hostname });
  }

  // FundElite / self-repair / credit-repair: keep existing dashboard behavior
  if (user) {
    const role = String(user.role || '').toLowerCase();
    if (isPlatformAdmin(user) || isPlatformConsoleRole(role)) {
      return '/platform/dashboard';
    }
    if (user.agency_id && (role === 'owner' || role === 'agency_owner' || role === 'admin')) {
      return `/agency/${user.agency_id}/dashboard`;
    }
  }

  return '/dashboard';
}

/**
 * Resolve post-login destination when on the FundGenius app host.
 * Returns null when the host is not FundGenius (caller should use default routing).
 */
export function getFundGeniusPostLoginPath({
  user,
  supportContext = null,
  isInSupportMode = false,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
} = {}) {
  if (!isFundGeniusAppLoginHost(hostname) || !user) return null;

  const portal = String(supportContext?.effective_portal_type || '').toLowerCase();
  const agencyId = supportContext?.effective_agency_id;

  if (
    isInSupportMode
    && (portal === 'fundgenius_hq' || portal === 'fundgenius')
  ) {
    if (agencyId) {
      return `${FUNDGENIUS_HQ_CLIENTS_PATH}?agency_id=${encodeURIComponent(agencyId)}`;
    }
    return FUNDGENIUS_HQ_CLIENTS_PATH;
  }

  // Platform admins/support use platform HQ dashboard; agency owners use app workspace.
  return getPortalLandingRoute({ hostname, user });
}

export default {
  isFundGeniusAppLoginHost,
  isFundGeniusSignupHost,
  getFundGeniusLoginBranding,
  getFundGeniusPostLoginPath,
  getPostSignupRedirectPath,
  sanitizeAuthRedirectUrl,
  clearStaleAuthRedirectStorage,
  FUNDGENIUS_HQ_CLIENTS_PATH,
  FUNDGENIUS_APP_CLIENTS_PATH,
};
